<template>
  <v-container>
    <v-row class="mb-6">
      <v-col cols="12">
        <h1>Settings</h1>
        <p class="text-body2 text-medium-emphasis">Organization integrations</p>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" md="8">
        <v-card>
          <v-card-item>
            <template #title>Invoice Ninja</template>
            <template #append>
              <v-chip
                size="small"
                :color="connected ? 'success' : undefined"
              >
                {{ connected ? 'Connected' : 'Not connected' }}
              </v-chip>
            </template>
          </v-card-item>
          <v-card-text>
            <p v-if="connected" class="text-body2 mb-4">
              Connected to
              <strong>{{ org?.invoiceNinjaCompanyName || org?.invoiceNinjaBaseUrl }}</strong>.
              Re-enter the token below to update the connection.
            </p>

            <v-alert
              v-if="inStore.error"
              type="error"
              closable
              class="mb-4"
              @click:close="inStore.error = null"
            >
              {{ inStore.error }}
            </v-alert>
            <v-alert v-if="connectMsg" type="success" closable class="mb-4" @click:close="connectMsg = null">
              {{ connectMsg }}
            </v-alert>

            <v-form @submit.prevent="doConnect">
              <v-text-field
                v-model="form.baseUrl"
                label="Base URL"
                placeholder="https://billing.example.com"
                hint="Your self-hosted Invoice Ninja URL, no trailing /api/v1"
                persistent-hint
                class="mb-3"
              />
              <v-text-field
                v-model="form.token"
                label="API token"
                type="password"
                class="mb-3"
              />
              <v-text-field
                v-model="form.secret"
                label="API secret (optional)"
                type="password"
                hint="Only if your instance sets API_SECRET"
                persistent-hint
                class="mb-4"
              />
              <v-btn color="primary" type="submit" :loading="inStore.isConnecting">
                Test &amp; Connect
              </v-btn>
            </v-form>
          </v-card-text>

          <template v-if="connected">
            <v-divider />
            <v-card-text>
              <p class="text-subtitle-2 mb-2">Import</p>
              <p class="text-body2 text-medium-emphasis mb-3">
                Pulls clients from Invoice Ninja into Gigdriver. Safe to re-run —
                it refreshes name / contact / address and never removes clients.
              </p>
              <v-btn
                variant="tonal"
                :loading="clientsStore.isImporting"
                @click="importClients"
              >
                <v-icon start>mdi-download</v-icon>
                Import clients
              </v-btn>
              <p v-if="importMsg" class="text-body2 text-success mt-3">{{ importMsg }}</p>
              <p v-if="clientsStore.error" class="text-body2 text-error mt-3">
                {{ clientsStore.error }}
              </p>
            </v-card-text>
          </template>
        </v-card>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" md="8">
        <v-card>
          <v-card-item>
            <template #title>Labor Quoting</template>
          </v-card-item>
          <v-card-text>
            <p class="text-body2 text-medium-emphasis mb-3">
              Default cushion added on top of each position's budget (or its actual offered cost,
              whichever is higher) when computing a gig's labor quote. Overridable per gig.
            </p>
            <v-text-field
              v-model.number="marginForm.defaultLaborMarginPct"
              label="Default Margin %"
              type="number"
              density="compact"
              style="max-width: 160px"
              @change="saveMarginSettings"
            />
            <p v-if="marginSavedMsg" class="text-body2 text-success mb-0">{{ marginSavedMsg }}</p>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" md="8">
        <v-card>
          <v-card-item>
            <template #title>Holidays</template>
          </v-card-item>
          <v-card-text>
            <p class="text-body2 text-medium-emphasis mb-3">
              Recognized holidays bill as overtime for the entire call/day, for any rate-card role
              that's opted into the standard timekeeping policy (see Rate Cards).
            </p>
            <div v-for="(h, i) in holidayForm.holidays" :key="i" class="mb-2">
              <v-row dense align="center">
                <v-col cols="6" sm="4">
                  <v-text-field v-model="h.date" label="Date" type="date" density="compact" hide-details />
                </v-col>
                <v-col cols="6" sm="5">
                  <v-text-field v-model="h.name" label="Name" density="compact" hide-details />
                </v-col>
                <v-col cols="12" sm="3" class="text-right">
                  <v-btn icon="mdi-close" size="x-small" variant="text" @click="holidayForm.holidays.splice(i, 1)" />
                </v-col>
              </v-row>
            </div>
            <v-btn size="small" variant="tonal" class="mb-3" @click="addHoliday">
              <v-icon start size="small">mdi-plus</v-icon>Add Holiday
            </v-btn>
            <div>
              <v-btn color="primary" size="small" :loading="savingHolidays" @click="saveHolidaySettings">Save Holidays</v-btn>
              <p v-if="holidaysSavedMsg" class="text-body2 text-success mt-2 mb-0">{{ holidaysSavedMsg }}</p>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" md="8">
        <v-card>
          <v-card-item>
            <template #title>Organization Branding</template>
          </v-card-item>
          <v-card-text>
            <p class="text-body2 text-medium-emphasis mb-3">
              Shown on contracts sent for signature — the generated PDF and the client's public
              signing page. PNG or JPEG, up to 2MB.
            </p>
            <div v-if="logoPreviewUrl" class="mb-3">
              <img :src="logoPreviewUrl" alt="Organization logo" class="logo-preview" />
            </div>
            <v-file-input
              v-model="logoFile"
              label="Upload logo"
              accept="image/png,image/jpeg"
              density="compact"
              prepend-icon="mdi-image"
              :loading="uploadingLogo"
              style="max-width: 320px"
              @update:model-value="handleLogoSelected"
            />
            <v-alert v-if="logoError" type="error" variant="tonal" density="compact" class="mb-2">{{ logoError }}</v-alert>
            <p v-if="logoUploadMsg" class="text-body2 text-success mb-0">{{ logoUploadMsg }}</p>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" md="8">
        <v-card>
          <v-card-item>
            <template #title>Contract Terms</template>
          </v-card-item>
          <v-card-text>
            <p class="text-body2 text-medium-emphasis mb-3">
              Standing terms (cancellation policy, payment terms, liability language, etc.) attached
              to every contract sent for signature, alongside its labor-quote line items. Editing
              this never changes a contract that's already been sent or signed.
            </p>
            <div v-if="termsEditor" class="terms-editor">
              <div class="terms-toolbar">
                <v-btn
                  size="small"
                  variant="text"
                  :color="termsEditor.isActive('bold') ? 'primary' : undefined"
                  @click="termsEditor.chain().focus().toggleBold().run()"
                >
                  <v-icon>mdi-format-bold</v-icon>
                </v-btn>
                <v-btn
                  size="small"
                  variant="text"
                  :color="termsEditor.isActive('italic') ? 'primary' : undefined"
                  @click="termsEditor.chain().focus().toggleItalic().run()"
                >
                  <v-icon>mdi-format-italic</v-icon>
                </v-btn>
                <v-btn
                  size="small"
                  variant="text"
                  :color="termsEditor.isActive('heading', { level: 1 }) ? 'primary' : undefined"
                  @click="termsEditor.chain().focus().toggleHeading({ level: 1 }).run()"
                >
                  H1
                </v-btn>
                <v-btn
                  size="small"
                  variant="text"
                  :color="termsEditor.isActive('heading', { level: 2 }) ? 'primary' : undefined"
                  @click="termsEditor.chain().focus().toggleHeading({ level: 2 }).run()"
                >
                  H2
                </v-btn>
                <v-btn
                  size="small"
                  variant="text"
                  :color="termsEditor.isActive('heading', { level: 3 }) ? 'primary' : undefined"
                  @click="termsEditor.chain().focus().toggleHeading({ level: 3 }).run()"
                >
                  H3
                </v-btn>
                <v-btn
                  size="small"
                  variant="text"
                  :color="termsEditor.isActive('bulletList') ? 'primary' : undefined"
                  @click="termsEditor.chain().focus().toggleBulletList().run()"
                >
                  <v-icon>mdi-format-list-bulleted</v-icon>
                </v-btn>
                <v-btn
                  size="small"
                  variant="text"
                  :color="termsEditor.isActive('orderedList') ? 'primary' : undefined"
                  @click="termsEditor.chain().focus().toggleOrderedList().run()"
                >
                  <v-icon>mdi-format-list-numbered</v-icon>
                </v-btn>
              </div>
              <editor-content :editor="termsEditor" class="terms-content" />
            </div>
            <div class="mt-3">
              <v-btn color="primary" size="small" :loading="savingTerms" @click="saveContractTerms">Save Terms</v-btn>
              <p v-if="termsSavedMsg" class="text-body2 text-success mt-2 mb-0">{{ termsSavedMsg }}</p>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" md="8">
        <v-card>
          <v-card-item>
            <template #title>Offer &amp; Invite Emails</template>
          </v-card-item>
          <v-card-text>
            <p class="text-body2 text-medium-emphasis mb-3">
              Where a reply lands — including the automatic RSVP a calendar app sends when someone
              clicks Accept/Decline on a calendar invite instead of responding in Gigdriver. The
              sending address itself can't receive mail, so without this, both hard-bounce back to
              whoever tried. Defaults to your (the owner's) account email if left blank.
            </p>
            <v-text-field
              v-model="replyToForm.replyToEmail"
              label="Reply-To Address"
              type="email"
              :placeholder="authStore.currentUser?.email"
              density="compact"
              style="max-width: 320px"
              @change="saveReplyToSettings"
            />
            <p v-if="replyToSavedMsg" class="text-body2 text-success mb-2">{{ replyToSavedMsg }}</p>

            <p class="text-body2 text-medium-emphasis mb-3 mt-4">
              These emails are built on the server, which has no browser and so no local time zone
              of its own — without this set, call times in them default to UTC and can read hours
              off from what's actually on the gig. Set your venues' time zone so times shown in
              offer and calendar-invite emails match what's in Gigdriver. (The calendar invite file
              itself is unaffected — that's always timezone-correct regardless of this setting.)
            </p>
            <v-autocomplete
              v-model="tzForm.timezone"
              :items="timeZoneOptions"
              label="Time Zone"
              :placeholder="browserTimezone"
              density="compact"
              clearable
              style="max-width: 320px"
              @update:model-value="saveTimezoneSettings"
            />
            <p v-if="tzSavedMsg" class="text-body2 text-success mb-0">{{ tzSavedMsg }}</p>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" md="8">
        <v-card>
          <v-card-item>
            <template #title>Item Catalog</template>
            <template #append>
              <v-btn
                v-if="connected"
                variant="tonal"
                size="small"
                :loading="itemsStore.isImporting"
                @click="importItems"
              >
                <v-icon start size="small">mdi-download</v-icon>
                Import from Invoice Ninja
              </v-btn>
            </template>
          </v-card-item>
          <v-card-text>
            <p class="text-body2 text-medium-emphasis mb-3">
              Products used as quote line items. Import from Invoice Ninja or add
              your own — either way they're editable here.
            </p>
            <v-alert v-if="itemsStore.error" type="error" closable class="mb-3" @click:close="itemsStore.error = null">
              {{ itemsStore.error }}
            </v-alert>
            <p v-if="itemImportMsg" class="text-body2 text-success mb-3">{{ itemImportMsg }}</p>

            <v-table v-if="itemsStore.items.length" density="compact" class="mb-4">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Details</th>
                  <th class="text-right">Unit Cost</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in itemsStore.items" :key="item.id">
                  <td>{{ item.name }}</td>
                  <td class="text-caption text-medium-emphasis" style="white-space: pre-line">
                    {{ item.description || '—' }}
                  </td>
                  <td class="text-right">${{ item.unitCost }}</td>
                  <td class="text-right">
                    <v-btn
                      icon="mdi-pencil"
                      size="x-small"
                      variant="text"
                      color="primary"
                      @click="openEditItem(item)"
                    />
                    <v-btn
                      icon="mdi-delete"
                      size="x-small"
                      variant="text"
                      color="error"
                      @click="removeItem(item.id)"
                    />
                  </td>
                </tr>
              </tbody>
            </v-table>
            <p v-else class="text-body2 text-medium-emphasis mb-4">No items yet.</p>

            <v-btn variant="tonal" size="small" @click="openNewItem">
              <v-icon start size="small">mdi-plus</v-icon>
              Add Item
            </v-btn>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog v-model="showItemDialog" width="450">
      <v-card>
        <v-card-title>{{ editingItemId ? 'Edit Item' : 'Add Item' }}</v-card-title>
        <v-card-text>
          <v-form @submit.prevent="saveItem">
            <v-text-field v-model="itemForm.name" label="Name" required class="mb-3" />
            <v-textarea v-model="itemForm.description" label="Description" rows="2" class="mb-3" />
            <v-text-field
              v-model.number="itemForm.unitCost"
              label="Unit Cost"
              type="number"
              prefix="$"
              class="mb-3"
            />
            <div class="text-right">
              <v-btn text @click="showItemDialog = false">Cancel</v-btn>
              <v-btn color="primary" type="submit" :loading="savingItem">Save</v-btn>
            </div>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<style scoped>
/* Tiptap ships no styling of its own — everything else on this page is a
   plain Vuetify component that doesn't need this, but a rich-text box has
   no off-the-shelf Vuetify equivalent. */
.terms-editor {
  border: 1px solid rgba(0, 0, 0, 0.23);
  border-radius: 4px;
}
.terms-toolbar {
  display: flex;
  gap: 2px;
  padding: 4px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}
.terms-content {
  padding: 12px;
  min-height: 160px;
}
.terms-content :deep(.ProseMirror) {
  outline: none;
  min-height: 140px;
}
.terms-content :deep(p) {
  margin: 0 0 0.75em;
}
.terms-content :deep(ul),
.terms-content :deep(ol) {
  margin: 0 0 0.75em;
  padding-left: 1.5em;
}
.logo-preview {
  max-height: 80px;
  max-width: 240px;
  object-fit: contain;
}
</style>

<script setup lang="ts">
import { ref, reactive, computed, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useEditor, EditorContent } from '@tiptap/vue-3';
import StarterKit from '@tiptap/starter-kit';
import DOMPurify from 'dompurify';
import type { Item } from '@gigdriver/domain';
import { dateKey } from '@gigdriver/domain';
import { uploadOrgLogo, getOrgLogoUrl } from '@gigdriver/client';
import { useOrganizationStore } from '../stores/organization';
import { useInvoiceNinjaStore } from '../stores/invoiceNinja';
import { useClientsStore } from '../stores/clients';
import { useItemsStore } from '../stores/items';
import { useAuthStore } from '../stores/auth';

const route = useRoute();
const orgStore = useOrganizationStore();
const inStore = useInvoiceNinjaStore();
const clientsStore = useClientsStore();
const itemsStore = useItemsStore();
const authStore = useAuthStore();

const orgId = () => route.query.org as string;
const org = computed(() => orgStore.currentOrg);
const connected = computed(() => org.value?.invoiceNinjaConnected === true);

const form = reactive({ baseUrl: '', token: '', secret: '' });
const connectMsg = ref<string | null>(null);
const importMsg = ref<string | null>(null);

const MAX_LOGO_BYTES = 2 * 1024 * 1024; // mirrors uploadOrgLogo's server-side cap
const logoFile = ref<File | File[] | null>(null);
const logoPreviewUrl = ref<string | null>(null);
const uploadingLogo = ref(false);
const logoUploadMsg = ref<string | null>(null);
const logoError = ref<string | null>(null);

async function loadLogoPreview() {
  if (!orgId()) return;
  try {
    const { url } = await getOrgLogoUrl({ orgId: orgId() });
    logoPreviewUrl.value = url;
  } catch {
    // Non-critical — the upload form still works without a preview.
  }
}

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

async function handleLogoSelected(value: File | File[] | null) {
  logoError.value = null;
  logoUploadMsg.value = null;
  const file = Array.isArray(value) ? value[0] : value;
  if (!file) return;
  if (!['image/png', 'image/jpeg'].includes(file.type)) {
    logoError.value = 'Logo must be a PNG or JPEG image.';
    logoFile.value = null;
    return;
  }
  if (file.size > MAX_LOGO_BYTES) {
    logoError.value = 'Logo must be under 2MB.';
    logoFile.value = null;
    return;
  }
  try {
    uploadingLogo.value = true;
    const dataUrl = await readFileAsDataUrl(file);
    const { url } = await uploadOrgLogo({ orgId: orgId(), dataUrl });
    logoPreviewUrl.value = url;
    logoUploadMsg.value = 'Saved.';
  } catch (err) {
    logoError.value = err instanceof Error ? err.message : 'Failed to upload logo.';
  } finally {
    uploadingLogo.value = false;
    logoFile.value = null;
  }
}

const marginForm = reactive({ defaultLaborMarginPct: 0 });
const marginSavedMsg = ref<string | null>(null);

async function saveMarginSettings() {
  marginSavedMsg.value = null;
  try {
    await orgStore.updateOrgSettings(orgId(), {
      defaultLaborMarginPct: marginForm.defaultLaborMarginPct || undefined,
    });
    marginSavedMsg.value = 'Saved.';
  } catch {
    // orgStore.error is set
  }
}

// isHoliday() (packages/domain/src/standard-timekeeping.ts) matches a
// holiday's date against a call by re-deriving each one's CALENDAR DAY IN
// THE ORG'S OWN TIME ZONE (dateKey(date, orgTimezone)), not raw UTC — so a
// midnight-UTC anchor round-trips wrong for any org west of UTC (i.e. every
// US time zone): dateKey('2026-12-25T00:00Z', 'America/Chicago') comes back
// '2026-12-24', a full day off. Anchoring at noon UTC instead keeps the
// instant within the same calendar day across the entire realistic range of
// IANA offsets this app's orgs actually use (up to roughly UTC-11/+11).
function toDateInputValue(d: Date): string {
  return dateKey(d, orgStore.currentOrg?.timezone ?? 'UTC');
}
function fromDateInputValue(s: string): Date {
  return new Date(`${s}T12:00:00Z`);
}
const holidayForm = reactive<{ holidays: { date: string; name: string }[] }>({ holidays: [] });
const savingHolidays = ref(false);
const holidaysSavedMsg = ref<string | null>(null);

function addHoliday() {
  holidayForm.holidays.push({ date: '', name: '' });
}

async function saveHolidaySettings() {
  holidaysSavedMsg.value = null;
  try {
    savingHolidays.value = true;
    const holidays = holidayForm.holidays
      .filter(h => h.date && h.name.trim())
      .map(h => ({ date: fromDateInputValue(h.date), name: h.name.trim() }));
    await orgStore.updateOrgSettings(orgId(), { holidays: holidays.length ? holidays : undefined });
    holidaysSavedMsg.value = 'Saved.';
  } catch {
    // orgStore.error is set
  } finally {
    savingHolidays.value = false;
  }
}

// StarterKit's node/mark set is intentionally kept in lockstep with
// functions/src/pdf.ts's HTML walker (bold/italic, h1-h3, bullet/numbered
// lists) — anything Tiptap can produce that the PDF renderer doesn't
// understand would silently drop from the archival PDF.
const termsEditor = useEditor({
  extensions: [StarterKit],
  content: '',
});
const savingTerms = ref(false);
const termsSavedMsg = ref<string | null>(null);

async function saveContractTerms() {
  termsSavedMsg.value = null;
  if (!termsEditor.value) return;
  try {
    savingTerms.value = true;
    // Sanitize before it ever reaches Firestore — defense in depth, since
    // this string is later rendered via v-html on the public sign page
    // (SignContract.vue, which also sanitizes again immediately before
    // rendering).
    const html = DOMPurify.sanitize(termsEditor.value.getHTML());
    await orgStore.updateOrgSettings(orgId(), { contractTermsHtml: html || undefined });
    termsSavedMsg.value = 'Saved.';
  } catch {
    // orgStore.error is set
  } finally {
    savingTerms.value = false;
  }
}

const replyToForm = reactive({ replyToEmail: '' });
const replyToSavedMsg = ref<string | null>(null);

async function saveReplyToSettings() {
  replyToSavedMsg.value = null;
  try {
    await orgStore.updateOrgSettings(orgId(), {
      replyToEmail: replyToForm.replyToEmail.trim() || undefined,
    });
    replyToSavedMsg.value = 'Saved.';
  } catch {
    // orgStore.error is set
  }
}

const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
const timeZoneOptions: string[] = (() => {
  try {
    return Intl.supportedValuesOf('timeZone');
  } catch {
    return [browserTimezone];
  }
})();
const tzForm = reactive<{ timezone: string | null }>({ timezone: null });
const tzSavedMsg = ref<string | null>(null);

async function saveTimezoneSettings() {
  tzSavedMsg.value = null;
  try {
    await orgStore.updateOrgSettings(orgId(), {
      timezone: tzForm.timezone ?? undefined,
    });
    tzSavedMsg.value = 'Saved.';
  } catch {
    // orgStore.error is set
  }
}

const showItemDialog = ref(false);
const savingItem = ref(false);
const editingItemId = ref<string | null>(null);
const itemForm = reactive({ name: '', description: '', unitCost: 0 });
const itemImportMsg = ref<string | null>(null);

onMounted(async () => {
  if (orgId() && orgStore.currentOrg?.id !== orgId()) {
    await orgStore.selectOrganization(orgId());
  }
  form.baseUrl = orgStore.currentOrg?.invoiceNinjaBaseUrl ?? '';
  marginForm.defaultLaborMarginPct = orgStore.currentOrg?.defaultLaborMarginPct ?? 0;
  replyToForm.replyToEmail = orgStore.currentOrg?.replyToEmail ?? '';
  tzForm.timezone = orgStore.currentOrg?.timezone ?? null;
  holidayForm.holidays = (orgStore.currentOrg?.holidays ?? []).map(h => ({ date: toDateInputValue(h.date), name: h.name }));
  termsEditor.value?.commands.setContent(orgStore.currentOrg?.contractTermsHtml ?? '');
  if (orgId()) itemsStore.loadOrgItems(orgId());
  await loadLogoPreview();
});

async function doConnect() {
  connectMsg.value = null;
  try {
    const r = await inStore.connect({
      orgId: orgId(),
      baseUrl: form.baseUrl.trim(),
      token: form.token.trim(),
      secret: form.secret.trim() || undefined,
    });
    connectMsg.value = r.companyName
      ? `Connected to "${r.companyName}".`
      : 'Connected.';
    form.token = '';
    form.secret = '';
  } catch {
    // inStore.error is set
  }
}

async function importClients() {
  importMsg.value = null;
  try {
    const r = await clientsStore.importFromInvoiceNinja(orgId());
    importMsg.value = `Imported ${r.imported} new, updated ${r.updated} (of ${r.total}).`;
  } catch {
    // clientsStore.error is set
  }
}

async function importItems() {
  itemImportMsg.value = null;
  try {
    const r = await itemsStore.importFromInvoiceNinja(orgId());
    itemImportMsg.value = `Imported ${r.imported} new, updated ${r.updated} (of ${r.total}).`;
  } catch {
    // itemsStore.error is set
  }
}

function openNewItem() {
  editingItemId.value = null;
  itemForm.name = '';
  itemForm.description = '';
  itemForm.unitCost = 0;
  showItemDialog.value = true;
}

function openEditItem(item: Item) {
  editingItemId.value = item.id;
  itemForm.name = item.name;
  itemForm.description = item.description ?? '';
  itemForm.unitCost = item.unitCost;
  showItemDialog.value = true;
}

async function saveItem() {
  try {
    savingItem.value = true;
    const payload = {
      name: itemForm.name,
      description: itemForm.description || undefined,
      unitCost: itemForm.unitCost || 0,
    };
    if (editingItemId.value) {
      await itemsStore.updateItem(orgId(), editingItemId.value, payload);
    } else {
      await itemsStore.createItem(orgId(), payload);
    }
    showItemDialog.value = false;
  } catch {
    // itemsStore.error is set
  } finally {
    savingItem.value = false;
  }
}

async function removeItem(itemId: string) {
  if (!confirm('Delete this item?')) return;
  await itemsStore.deleteItem(orgId(), itemId);
}
</script>
